import type { QueryClient } from '@tanstack/react-query'

import { marketEndpoints } from '@/api/endpointCache'
import {
  breadthFromWire,
  chartPoints,
  displayDate,
  displayTime,
  indexDisplayName,
  kittaToCrore,
  marketStateIsOpen,
  rupeesToArba,
  rupeesToCrore,
  sectorSlug,
  shortMarketLabel,
  sparklineValues,
} from '@/features/market/api/marketMappers'
import { marketOverviewSchema, type MarketOverview } from '@/schemas/market-overview'

const TOP_BROKERS = 7

const rangeCaption = (current: number, low: number, high: number): string | null => {
  if (high <= low) return null
  const position = (current - low) / (high - low)
  if (position < 1 / 3) return 'lower third of the year'
  if (position > 2 / 3) return 'upper third of the year'
  return 'middle third of the year'
}

export const overviewService = {
  overview: async (client?: QueryClient): Promise<MarketOverview> => {
    const api = marketEndpoints(client)
    // Eight endpoints, not ten: `/market/events/` and `/market/updates/` fed
    // `comingUp` and `updates`, which nothing on this tab draws — the rail
    // reads updates through its own query, and "Coming up" lives on /market/events.
    const [indices, history, summary, status, gainers, losers, sectors, brokers] =
      await Promise.all([
        api.indices(),
        api.indexHistory('NEPSE', '1M'),
        api.summary(),
        api.status(),
        api.movers({ type: 'gainers', limit: 5 }),
        api.movers({ type: 'losers', limit: 5 }),
        api.sectors('change'),
        // Seven, the frame's "Top brokers today" count; mm-be ranks them by turnover.
        api.brokers(TOP_BROKERS),
      ])
    const nepse = indices.rows.find((row) => row.key === 'NEPSE')
    if (nepse === undefined) throw new Error('NEPSE index is missing from the market feed')
    const week52 = summary.week52
    const asOf = summary.as_of ?? status.as_of
    const range52 =
      week52 === null
        ? null
        : {
            now: week52.current,
            low: week52.low,
            high: week52.high,
            caption:
              week52.low === null || week52.high === null
                ? null
                : rangeCaption(Number(week52.current), Number(week52.low), Number(week52.high)),
          }

    return marketOverviewSchema.parse({
      indexTiles: indices.rows.map((row) => ({
        id: row.key.toLowerCase(),
        key: row.key,
        label: indexDisplayName(row.name, row.key),
        name: row.name,
        value: row.value,
        pointChange: row.change,
        percentChange: row.change_percent,
      })),
      headline: {
        index: indexDisplayName(nepse.name, nepse.key),
        indexKey: nepse.key,
        value: nepse.value,
        pointChange: nepse.change,
        percentChange: nepse.change_percent,
        asOf: { date: displayDate(asOf), time: displayTime(asOf) },
        isOpen: marketStateIsOpen(status.state),
        turnoverCr: rupeesToCrore(summary.turnover),
        volumeCrKitta: kittaToCrore(summary.shares_traded),
        breadth: breadthFromWire(summary.breadth),
        points: chartPoints(history.points),
      },
      stats: {
        turnoverArba: rupeesToArba(summary.turnover),
        sharesTradedCrKitta: kittaToCrore(summary.shares_traded),
        transactions: summary.transactions,
        scripsTraded: summary.scrips_traded,
        totalMarketCap: summary.total_market_cap,
      },
      breadth: breadthFromWire(summary.breadth),
      range52,
      topGainers: gainers.rows.map((row) => ({
        symbol: row.key,
        name: row.name,
        ltp: row.ltp,
        percentChange: row.change_percent,
        sparkline: sparklineValues(row.sparkline),
        volume: row.volume,
      })),
      topLosers: losers.rows.map((row) => ({
        symbol: row.key,
        name: row.name,
        ltp: row.ltp,
        percentChange: row.change_percent,
        sparkline: sparklineValues(row.sparkline),
        volume: row.volume,
      })),
      sectorsToday: sectors.rows.map((row) => ({
        id: sectorSlug(row.key),
        name: shortMarketLabel(row.name),
        percentChange: row.change_percent,
      })),
      floorsheetHighlights: brokers.rows.map((row) => ({
        code: row.code,
        name: row.name ?? `Broker ${row.code}`,
        mostActiveIn: null,
        netKitta: row.net_qty,
      })),
      mitraSays: 'A market summary is context for the session, not investment advice.',
    })
  },
}
